import { Button, ClickAwayListener, Menu, MenuItem } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { chunk } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AppRoute } from 'src/core/AppRoute';
import DiscoveryList from './info/DiscoveryList';

const StyledMenu = styled(Menu)(({ theme }) => ({
    zIndex: theme.zIndex.appBar - 1,
    maxHeight: '80vh',
    '& [class*="MuiPaper-root"]': {
        padding: theme.spacing(3),
        zIndex: 1300,
    },
}));

const MegamenuRow = styled('div')(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
    },
}));

const MegamenuDiscovery = styled('div')(({ theme }) => ({
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    [theme.breakpoints.up('lg')]: {
        width: '500px',
        borderWidth: '0 0 0 1px',
        paddingLeft: theme.spacing(2),
    },
    [theme.breakpoints.down('lg')]: {
        flexGrow: 1,
        borderWidth: '1px 0 0 0',
        paddingTop: theme.spacing(2),
    },
}));

const Thumbnail = styled('img')({
    width: '100%',
    height: '120px',
    objectPosition: 'center',
    objectFit: 'cover',
});

const TitleText = styled('div')(({ theme }) => ({
    fontWeight: 900,
    fontSize: '1.0rem',
    lineHeight: '1.2rem',
    color: theme.palette.primary.main,
    wordBreak: 'break-word',
    wordWrap: 'break-word',
    width: '100%',
}));

const Text = styled('div')(({ theme }) => ({
    fontSize: '0.8rem',
    lineHeight: '1rem',
    color: theme.palette.primary.main,
}));

const RoutesColumn = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const RoutesRow = styled('div')(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    width: '180px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
}));

export interface NavMenuProps {
    title: string;
    items: AppRoute[];
    className?: string;
    showDiscovery?: boolean;
}

export const NavMenu: React.FC<NavMenuProps> = ({ title, items, className, showDiscovery }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleItemClick = (to: string) => {
        history.push(to);
        handleClose();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const id = `${title.replace(/ /g, '_ ').toLowerCase()}_menu}`;

    const menuItems = (
        <RoutesColumn>
            {chunk(items, showDiscovery ? 4 : 8).map((row, i) => (
                <RoutesRow key={i}>
                    {row.map(({ path, t_name, t_params, thumbnail }) => (
                        <StyledMenuItem key={`${i}-${path}`} onClick={() => handleItemClick(path)}>
                            {thumbnail && <Thumbnail src={thumbnail} alt={t(t_name, t_params)} />}
                            <TitleText>{t(t_name, t_params)}</TitleText>
                            <Text>{t('learn_more')}</Text>
                        </StyledMenuItem>
                    ))}
                </RoutesRow>
            ))}
        </RoutesColumn>
    );

    return (
        <>
            <ClickAwayListener onClickAway={handleClose}>
                <div>
                    <Button
                        color="inherit"
                        aria-controls={id}
                        aria-haspopup="true"
                        onClick={handleOpen}
                        className={className}>
                        {title}
                    </Button>

                    <StyledMenu
                        BackdropProps={{
                            invisible: true,
                        }}
                        id={id}
                        elevation={8}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        getContentAnchorEl={null}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        MenuListProps={{ disablePadding: true }}>
                        <MegamenuRow>
                            <div>{menuItems}</div>
                            {showDiscovery && (
                                <MegamenuDiscovery>
                                    <DiscoveryList />
                                </MegamenuDiscovery>
                            )}
                        </MegamenuRow>
                    </StyledMenu>
                </div>
            </ClickAwayListener>
        </>
    );
};
